import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useBackendReconnectKey } from "@/hooks/use-backend-connectivity";
import { fetchWithAuth } from "@/lib/utils";
import type { Session, SessionListResponse, SessionSearchResult, SessionSearchV2Response, QuickSessionSearchResponse, QuickSessionSearchResult } from "@/lib/session";

type SearchError = "timeout" | "unavailable" | "olderServer";
type State = {
  key: string; titles: QuickSessionSearchResult[]; content: SessionSearchResult[];
  titleLoading: boolean; contentLoading: boolean;
  titleError: SearchError | null; contentError: SearchError | null;
  hasMore: boolean; cursor: string | null; titleRoute: "quick" | "legacy";
  limitedCoverage: boolean; authDenied: boolean;
};
function routeAbsent(response: Response, body: unknown): boolean {
  if ([404, 405, 501].includes(response.status)) return true;
  if (response.status !== 422 || !body || typeof body !== "object") return false;
  const detail = (body as { detail?: unknown }).detail;
  return Array.isArray(detail) && detail.some((item) => {
    if (!item || typeof item !== "object") return false;
    const loc = (item as { loc?: unknown }).loc;
    return Array.isArray(loc) && loc[0] === "path" && loc.some((part) =>
      typeof part === "string" && /^(session_)?id$/.test(part));
  });
}
const normalize = (s: string) => s.normalize("NFKC").toLocaleLowerCase("en").trim();
async function fetchWithTimeout(
  url: string, getToken: () => Promise<string>, parentSignal: AbortSignal, timeoutMs: number,
): Promise<Response> {
  const request = new AbortController();
  const abort = () => request.abort();
  parentSignal.addEventListener("abort", abort, { once: true });
  if (parentSignal.aborted) request.abort();
  let timer: ReturnType<typeof setTimeout>;
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      request.abort();
      reject(new Error("search_timeout"));
    }, timeoutMs);
  });
  try {
    return await Promise.race([fetchWithAuth(url, { signal: request.signal }, getToken), deadline]);
  } finally {
    clearTimeout(timer!);
    parentSignal.removeEventListener("abort", abort);
  }
}
const searchable = (s: QuickSessionSearchResult) => !s.is_archived && s.session_type !== "sub_agent"
  && (!s.parent_session_id || s.session_type === "thread") && s.can_read !== false;
function titleResult(s: QuickSessionSearchResult): SessionSearchResult {
  return { id: s.id, title: s.title, last_active_at: s.last_active_at, agent_id: s.agent_id,
    match_type: "title", snippet: null, match_count: 1 };
}
export interface UseSessionSearchReturn {
  titleResults: SessionSearchResult[]; contentResults: SessionSearchResult[];
  titleLoading: boolean; contentLoading: boolean;
  titleError: SearchError | null; contentError: SearchError | null;
  hasMore: boolean; loadMore: () => void;
  limitedCoverage: boolean; retryContent: () => void;
}
export function useSessionSearch(
  query: string, getToken: () => Promise<string>, loadedSessions: Session[] = [], scopeKey = "",
): UseSessionSearchReturn {
  const reconnect = useBackendReconnectKey();
  const q = normalize(query).slice(0, 200);
  const key = JSON.stringify([scopeKey, q, reconnect]);
  const [state, setState] = useState<State | null>(null);
  const [retryEpoch, setRetryEpoch] = useState(0);
  const sequence = useRef(0);
  const controller = useRef<AbortController | null>(null);
  const cache = useRef(new Map<string, Pick<State, "titles" | "hasMore" | "cursor" | "titleRoute" | "limitedCoverage">>());
  const fingerprint = loadedSessions.map((s) => [s.id, s.title, s.is_archived, s.last_active_at, s.can_read].join(":")).join("|");
  useEffect(() => { cache.current.clear(); }, [scopeKey, fingerprint]);

  useEffect(() => {
    const id = ++sequence.current;
    controller.current?.abort();
    if (!q) { setState(null); return; }
    const abort = new AbortController();
    controller.current = abort;
    const cached = cache.current.get(key);
    setState({ key, titles: cached?.titles ?? [], content: [], titleLoading: !cached,
      contentLoading: true, titleError: null, contentError: null,
      hasMore: cached?.hasMore ?? false, cursor: cached?.cursor ?? null,
      titleRoute: cached?.titleRoute ?? "quick", limitedCoverage: cached?.limitedCoverage ?? false,
      authDenied: false });
    const current = () => sequence.current === id && !abort.signal.aborted;
    const deny = () => {
      cache.current.clear();
      if (current()) setState((s) => s?.key === key
        ? { ...s, titles: [], content: [], authDenied: true } : s);
    };
    const titleTimer = cached ? null : setTimeout(async () => {
      try {
        const params = new URLSearchParams({ q, limit: "50" });
        let response: Response | null = null;
        let data: QuickSessionSearchResponse | SessionListResponse | null = null;
        let legacy = false;
        try {
          response = await fetchWithTimeout("/api/sessions/quick-search?" + params, getToken, abort.signal, 3000);
          if ([401, 403].includes(response.status)) {
            deny();
            throw new Error("auth_denied");
          }
          data = await response.json() as QuickSessionSearchResponse;
          legacy = routeAbsent(response, data) || response.status >= 500
            || "error" in data && data.error === "search_timeout";
        } catch (error) {
          if (!current() || error instanceof Error && error.message === "auth_denied") throw error;
          legacy = true;
        }
        if (legacy) {
          response = await fetchWithTimeout("/api/sessions?" + params, getToken, abort.signal, 5000);
          if ([401, 403].includes(response.status)) deny();
          data = await response.json() as SessionListResponse;
        }
        if (!response?.ok || !data || !Array.isArray(data.sessions) || "error" in data && data.error)
          throw new Error("title request failed");
        if (!current()) return;
        const page = { titles: data.sessions.filter(searchable), hasMore: Boolean(data.has_more && data.next_cursor),
          cursor: data.next_cursor ?? null, titleRoute: legacy ? "legacy" as const : "quick" as const,
          limitedCoverage: legacy };
        cache.current.set(key, page);
        if (cache.current.size > 20) cache.current.delete(cache.current.keys().next().value!);
        setState((s) => s?.key === key ? { ...s, ...page, titleLoading: false, titleError: null } : s);
      } catch {
        if (current()) setState((s) => s?.key === key
          ? { ...s, titleLoading: false, titleError: "unavailable" } : s);
      }
    }, 90);
    const contentTimer = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ q, limit: "20" });
        const response = await fetchWithTimeout("/api/sessions/search-v2?" + params, getToken, abort.signal, 3000);
        if ([401, 403].includes(response.status)) deny();
        const data = await response.json() as SessionSearchV2Response;
        if (!current()) return;
        if (routeAbsent(response, data)) {
          setState((s) => s?.key === key ? { ...s, contentLoading: false, contentError: "olderServer" } : s);
        } else if (data.error === "search_timeout") {
          setState((s) => s?.key === key ? { ...s, contentLoading: false, contentError: "timeout" } : s);
        } else if (!response.ok) {
          throw new Error("content request failed");
        } else if (data.content_scope !== "user_assistant" || !Array.isArray(data.results)
          || data.results.some((r) => r.match_type === "content"
            && (!r.message_id || !["user", "assistant"].includes(r.role ?? "")))) {
          throw new Error("invalid content response");
        } else {
          setState((s) => s?.key === key ? { ...s, contentLoading: false, contentError: null,
            content: data.results.filter((r) => r.match_type === "content" && r.message_id) } : s);
        }
      } catch (error) {
        if (current()) setState((s) => s?.key === key
          ? { ...s, contentLoading: false, contentError: error instanceof Error && error.message === "search_timeout"
            ? "timeout" : "unavailable" } : s);
      }
    }, 200);
    return () => { if (titleTimer) clearTimeout(titleTimer); clearTimeout(contentTimer); abort.abort(); };
  }, [key, q, getToken, retryEpoch]);

  const loadMore = useCallback(async () => {
    if (!state || state.key !== key || !state.hasMore || !state.cursor || state.titleLoading) return;
    const id = sequence.current;
    const params = new URLSearchParams({ q, limit: "50",
      [state.titleRoute === "quick" ? "cursor" : "before"]: state.cursor });
    setState((s) => s?.key === key ? { ...s, titleLoading: true } : s);
    try {
      const response = await fetchWithTimeout((state.titleRoute === "quick"
        ? "/api/sessions/quick-search?" : "/api/sessions?") + params,
        getToken, controller.current!.signal, 5000);
      if ([401, 403].includes(response.status)) {
        cache.current.clear();
        setState((s) => s?.key === key ? { ...s, titles: [], content: [], authDenied: true } : s);
      }
      if (!response.ok) throw new Error("title page failed");
      const data = await response.json() as SessionListResponse;
      if (!Array.isArray(data.sessions)) throw new Error("invalid title page");
      if (sequence.current !== id || controller.current?.signal.aborted) return;
      setState((s) => s?.key === key ? { ...s, titles: [...s.titles, ...data.sessions.filter(searchable)],
        hasMore: Boolean(data.has_more && data.next_cursor), cursor: data.next_cursor ?? null,
        titleLoading: false, titleError: null } : s);
    } catch {
      if (sequence.current === id && !controller.current?.signal.aborted)
        setState((s) => s?.key === key ? { ...s, titleLoading: false, titleError: "unavailable" } : s);
    }
  }, [getToken, key, q, state]);

  const titleResults = useMemo(() => {
    if (!q || state?.key === key && state.authDenied) return [];
    const byId = new Map<string, QuickSessionSearchResult>();
    if (state?.key === key) for (const s of state.titles) if (searchable(s)) byId.set(s.id, s);
    for (const s of loadedSessions) {
      if (searchable(s)) byId.set(s.id, s);
      else byId.delete(s.id);
    }
    return [...byId.values()].filter((s) => normalize(s.title).includes(q))
      .sort((a, b) => Date.parse(b.last_active_at) - Date.parse(a.last_active_at)
        || b.id.localeCompare(a.id))
      .slice(0, 100).map(titleResult);
  }, [loadedSessions, q, state, key]);
  const visible = state?.key === key ? state : null;
  const loadedById = new Map(loadedSessions.map((session) => [session.id, session]));
  return { titleResults, contentResults: visible?.content.filter((s) =>
    !visible?.authDenied && (!loadedById.has(s.id) || searchable(loadedById.get(s.id)!))) ?? [],
    titleLoading: Boolean(q) && (visible?.titleLoading ?? true),
    contentLoading: Boolean(q) && (visible?.contentLoading ?? true),
    titleError: visible?.titleError ?? null, contentError: visible?.contentError ?? null,
    hasMore: visible?.hasMore ?? false, loadMore, limitedCoverage: visible?.limitedCoverage ?? false,
    retryContent: () => setRetryEpoch((value) => value + 1) };
}
